import { useQuery } from "@tanstack/react-query";
import { useDynamicLocation } from "apps/commons/hooks/useDynamicLocation";
import { TanstackQueryKey } from "apps/commons/tanstackQueryKey";
import { oneOnboardingApi } from "libs/api";
import { useState } from "react";
import { type Path, useNavigate, useParams } from "router";
import { sidebarItemStatusMapper } from "./constants";
import { useSetSidebarAtom } from "./sidebarAtom";

export const useSidebarApi = () => {
    const { lang, propertyId } = useParams("/:lang/list-my-property/onboard/:propertyId/basics");
    const { pathname } = useDynamicLocation();
    const setSidebarAtom = useSetSidebarAtom();
    const [firstFetch, setFirstFetch] = useState(true);
    const navigate = useNavigate();

    useQuery({
        queryKey: [TanstackQueryKey.GetSidebarData, lang, pathname],
        queryFn: () => oneOnboardingApi.getSidebarData(lang, Number(propertyId), pathname).then((res) => res.data),
        enabled: pathname !== "/:lang/list-my-property/onboard/:propertyId/complete",
        onSuccess: (sidebar) => {
            setSidebarAtom(sidebar);

            const currentStep = sidebar.find((item) => item.path === pathname);

            if (
                // only validate this on first fetch
                firstFetch &&
                // run redirect logic if current page that user landed is not completed, if you cannot find step in sidebar then do nothing
                ((currentStep && currentStep.status !== sidebarItemStatusMapper.Completed) ||
                    // but do allow redirect logic to run on index page
                    pathname === "/:lang/list-my-property/onboard/:propertyId")
            ) {
                // condition 1:
                // if sidebar says property is published, force user to complete page
                const contractSidebar = sidebar.find(
                    (item) => (item.path as Path) === "/:lang/list-my-property/onboard/:propertyId/publish",
                );

                // condition 2:
                // if user landed on page beyond unfinished steps, redirect to the closest unfinished step
                const closestUnfinishedStep = sidebar.find((item) => item.status !== sidebarItemStatusMapper.Completed);
                const currentStepLanded =
                    sidebar.find((item) => item.path === pathname)?.order || Number.MAX_SAFE_INTEGER;

                if (contractSidebar.status === sidebarItemStatusMapper.Completed)
                    navigate("/:lang/list-my-property/onboard/:propertyId/complete", {
                        replace: true,
                        params: {
                            lang,
                            propertyId,
                        },
                    });
                else if (currentStepLanded > closestUnfinishedStep.order)
                    navigate(closestUnfinishedStep.path as Path, {
                        replace: true,
                        params: {
                            lang,
                            propertyId,
                        },
                    });

                setFirstFetch(false);
            }
        },
    });
};
